import { useEffect, useState } from 'react'
import { MicOff } from 'lucide-react'
import { UserAvatar } from '@/components/composites/user-avatar'
import { cn } from '@/lib/utils'
import type { TeamPerson } from './api'
import type { RemoteMedia } from './useProximityCalls'

/** True while the person on this stream is talking: a loudness reading a few times a second. */
function useSpeaking(stream: MediaStream | null): boolean {
  const [speaking, setSpeaking] = useState(false)
  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) return
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 256
    ctx.createMediaStreamSource(stream).connect(analyser)
    const buf = new Uint8Array(analyser.frequencyBinCount)
    const id = window.setInterval(() => {
      analyser.getByteFrequencyData(buf)
      const avg = buf.reduce((a, b) => a + b, 0) / buf.length
      setSpeaking(avg > 14)
    }, 200)
    return () => {
      window.clearInterval(id)
      void ctx.close()
    }
  }, [stream])
  return speaking
}

function Video({ stream, mirror }: { stream: MediaStream; mirror?: boolean }) {
  return (
    <video
      autoPlay
      playsInline
      muted
      className={cn('size-full object-cover', mirror && '-scale-x-100')}
      ref={(el) => {
        if (el && el.srcObject !== stream) el.srcObject = stream
      }}
    />
  )
}

function Tile({ name, person, stream, hasVideo, self }: { name: string; person?: TeamPerson; stream: MediaStream | null; hasVideo: boolean; self?: boolean }) {
  const speaking = useSpeaking(self ? null : stream)
  return (
    <li className={cn('relative size-24 overflow-hidden rounded-xl border-2 bg-muted shadow-sm', speaking ? 'border-success' : 'border-border')}>
      {stream && hasVideo ? (
        <Video stream={stream} mirror={self} />
      ) : (
        <div className="grid size-full place-items-center">
          <UserAvatar person={{ id: person?.userId ?? name, displayName: name, photoUrl: person?.photoUrl ?? null }} size="md" className="size-12" />
        </div>
      )}
      <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 truncate bg-foreground/55 px-1.5 py-0.5 text-micro font-medium text-text-inverse">
        {self ? 'You' : name.split(' ')[0]}
        {self && !stream?.getAudioTracks().length ? <MicOff className="size-3" /> : null}
      </span>
    </li>
  )
}

/** Who you are talking to right now: their camera, or their face when it is off, ringed while they speak. */
export function CallTiles({ calls, people, local, meName }: { calls: RemoteMedia[]; people: readonly TeamPerson[]; local: MediaStream | null; meName: string }) {
  if (calls.length === 0 && !local) return null
  return (
    <ul className="pointer-events-auto flex flex-wrap gap-2" aria-label="People you are talking to">
      {local ? <Tile name={meName} stream={local} hasVideo={local.getVideoTracks().length > 0} self /> : null}
      {calls.map((r) => {
        const person = people.find((p) => p.userId === r.userId)
        return <Tile key={r.userId} name={person?.displayName ?? 'Someone'} person={person} stream={r.stream} hasVideo={r.hasVideo} />
      })}
    </ul>
  )
}
